import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface SiteEvaluationRecord {
  id: number;
  SiteEvaluatorName: string;
  DateTime: string;
  Site: string;
  FinalizedDateTime: string;
  Period: string;
  Week: string;
  EvaluationStatus: string;
  ActionPlanStatus: string;
  Log: string;
}

export interface RecommendedActionPlan {
  Section: string;
  ActionPlan: string;
  "Assigned Supervisor": string;
}

export interface ActiveItem {
  Id: number;
  Location: string;
  Period: string;
  Week: string;
  actionPlanSection: string;
  assignedSupervisor: string;
  actionPlanComment: string;
  Created: string;
  Modified: string;
  CreatedBy: string;
  Site: string;
  ActionPlanStatus: string;
}

// Centralized error type for Site Evaluation
export interface SiteEvaluationError {
  id: string;
  apiName: string;
  message: string;
  timestamp?: number;
  context?: any;
  originalError?: any;
}

export interface CentralizedErrorState {
  errors: SiteEvaluationError[];
  activeError: SiteEvaluationError | null;
  isErrorModalVisible: boolean;
}

export interface SiteEvaluationState {
  tableData: SiteEvaluationRecord[];
  recommendedActionPlan: RecommendedActionPlan[];
  activeItemsData: ActiveItem[];
  selectedSiteEvaluation: SiteEvaluationRecord | null;
  // Centralized errors
  centralizedErrors: CentralizedErrorState;
}

const initialState: SiteEvaluationState = {
  tableData: [],
  recommendedActionPlan: [],
  activeItemsData: [],
  selectedSiteEvaluation: null,
  centralizedErrors: {
    errors: [],
    activeError: null,
    isErrorModalVisible: false,
  },
};

const siteEvaluationSlice = createSlice({
  name: "siteEvaluation",
  initialState,
  reducers: {
    setTableData(state, action: PayloadAction<SiteEvaluationRecord[]>) {
      state.tableData = action.payload;
    },
    setRecommendedActionPlan(
      state,
      action: PayloadAction<RecommendedActionPlan[]>
    ) {
      state.recommendedActionPlan = action.payload;
    },
    setActiveItemsData(state, action: PayloadAction<ActiveItem[]>) {
      state.activeItemsData = action.payload;
    },
    setSelectedSiteEvaluation(
      state,
      action: PayloadAction<SiteEvaluationRecord | null>
    ) {
      state.selectedSiteEvaluation = action.payload;
    },

    // Centralized error handling actions
    addCentralizedError(state, action: PayloadAction<SiteEvaluationError>) {
      state.centralizedErrors.errors.push(action.payload);
      // Keep only the last 10 errors
      if (state.centralizedErrors.errors.length > 10) {
        state.centralizedErrors.errors = state.centralizedErrors.errors.slice(-10);
      }
    },
    showErrorModal(state, action: PayloadAction<SiteEvaluationError>) {
      state.centralizedErrors.activeError = action.payload;
      state.centralizedErrors.isErrorModalVisible = true;
      // also add to list
      state.centralizedErrors.errors.push(action.payload);
      if (state.centralizedErrors.errors.length > 10) {
        state.centralizedErrors.errors = state.centralizedErrors.errors.slice(-10);
      }
    },
    hideErrorModal(state) {
      state.centralizedErrors.activeError = null;
      state.centralizedErrors.isErrorModalVisible = false;
    },
    setActiveError(state, action: PayloadAction<SiteEvaluationError | null>) {
      state.centralizedErrors.activeError = action.payload;
    },
    clearAllErrors(state) {
      state.centralizedErrors.errors = [];
      state.centralizedErrors.activeError = null;
      state.centralizedErrors.isErrorModalVisible = false;
    },
    removeErrorById(state, action: PayloadAction<string>) {
      state.centralizedErrors.errors = state.centralizedErrors.errors.filter(
        (error) => error.id !== action.payload
      );
      if (state.centralizedErrors.activeError?.id === action.payload) {
        state.centralizedErrors.activeError = null;
        state.centralizedErrors.isErrorModalVisible = false;
      }
    },
  },
});

export const {
  setTableData,
  setRecommendedActionPlan,
  setActiveItemsData,
  setSelectedSiteEvaluation,
  // error actions
  addCentralizedError,
  showErrorModal,
  hideErrorModal,
  setActiveError,
  clearAllErrors,
  removeErrorById,
} = siteEvaluationSlice.actions;
export default siteEvaluationSlice.reducer;
